/**
 * Sentry initialization and error reporting.
 * Must be imported first in the app entry (main.jsx).
 * Only initializes when VITE_SENTRY_DSN is set so dev/local builds work without Sentry.
 */
import * as Sentry from '@sentry/react';

const DSN = import.meta.env.VITE_SENTRY_DSN;
const isProd = import.meta.env.PROD;

if (DSN && typeof DSN === 'string' && DSN.trim() !== '') {
    Sentry.init({
        dsn: DSN.trim(),
        environment: import.meta.env.MODE || 'development',
        // Only send in production to avoid noise and respect quota
        enabled: isProd,
        // Sample rate for performance traces (keep low in prod)
        tracesSampleRate: isProd ? 0.1 : 1.0,
        // Don't send PII unless you explicitly need it
        sendDefaultPii: false,
        // Reduce console noise; set to true when debugging Sentry
        debug: false,
        // Ignore common non-actionable errors
        ignoreErrors: [
            'ResizeObserver loop',
            'Non-Error promise rejection',
            'Loading chunk',
            'ChunkLoadError',
            'Loading CSS chunk',
        ],
        beforeSend(event, hint) {
            const error = hint.originalException;
            if (error && typeof error === 'object' && error.message) {
                const msg = String(error.message);
                if (msg.includes('ResizeObserver') || msg.includes('chunk')) {
                    return null;
                }
            }
            return event;
        },
    });
}

/**
 * Report an error to Sentry (no-op if Sentry was not initialized).
 * Use from ErrorBoundary onError and anywhere else you want to report.
 * @param {Error} error
 * @param {{ componentStack?: string }} [errorInfo] - React error boundary info
 */
export function reportError(error, errorInfo) {
    if (!DSN || !error) return;
    try {
        Sentry.withScope((scope) => {
            if (errorInfo?.componentStack) {
                scope.setExtra('componentStack', errorInfo.componentStack);
            }
            Sentry.captureException(error);
        });
    } catch (e) {
        if (import.meta.env?.DEV && typeof console !== 'undefined') {
            console.warn('[Sentry] reportError failed:', e);
        }
    }
}

export default reportError;
